import "../../components/EventUserDashboard.css";
import AdminBackBar from "../../components/admin/AdminBackBar";
import Billede1 from "../../Images/Billede1.jpg";
import HeroSectionV2 from "../../components/HeroSectionV2";
import React, { useEffect, useState } from "react";
import {
    Select,
    Container,
    Paper,
    Title,
    Text,
    Stack,
    Grid,
    Group,
    Button,
    SegmentedControl,
    NumberInput,
    Divider,
    SimpleGrid,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { getAuthHeaders } from "./../../api/authHelper";

type ScopeType = "function" | "person";

type EventBar = {
    eventBarId: number;
    name: string;
    isActive: boolean;
};

type Invoice = {
    invoiceId: number;
    eventId: number;
    name: string;
    isActive: boolean;
};

const formatDate = (date: Date | null) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const dateRange = (start: Date, end: Date) => {
    const result: string[] = [];
    const current = new Date(start);

    while (current <= end) {
        result.push(formatDate(current));
        current.setDate(current.getDate() + 1);
    }

    return result;
};

const AdminDrinkAllowance: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL || "";

    const [scopeType, setScopeType] = useState<ScopeType>("function");

    const [events, setEvents] = useState<any[]>([]);
    const [list, setList] = useState<any[]>([]);

    const [eventId, setEventId] = useState<number>(0);
    const [scopeId, setScopeId] = useState<number>(0);

    const [mode, setMode] = useState<"single" | "range">("single");

    const [overview, setOverview] = useState<any[]>([]);

    const [dateRangeValue, setDateRangeValue] = useState<
        [Date | null, Date | null]
        >([new Date(), new Date()]);

    const [invoices, setInvoices] = useState<Invoice[]>([]);

    const [invoiceId, setInvoiceId] = useState<number | null>(null);
    const [bars, setBars] = useState<EventBar[]>([]);
    const [values, setValues] = useState<Record<number, number>>({});

    useEffect(() => {
        fetch(`${apiUrl}/api/events/light`, {
            headers: getAuthHeaders(),
        })
            .then(r => r.json())
            .then((data) => setEvents(Array.isArray(data) ? data : []))
            .catch(() => setEvents([]));
    }, [apiUrl]);

    const loadBars = async (eventId: number) => {
        const res = await fetch(`${apiUrl}/api/EventBars/event/${eventId}`);
        if (!res.ok) return;

        const data = await res.json();
        const safe = Array.isArray(data) ? data : [];

        setBars(safe);

        const init: Record<number, number> = {};
        safe.forEach((b: EventBar) => {
            init[b.eventBarId] = 0;
        });

        setValues(init);
    };

    const loadInvoices = async (eventId: number) => {
        const res = await fetch(`${apiUrl}/api/invoices/event/${eventId}`);

        if (!res.ok) {
            setInvoices([]);
            return;
        }

        const data = await res.json();

        const safe = Array.isArray(data) ? data : [];

        setInvoices(safe);
    };

    const loadScopeList = async (eventId: number) => {
        const endpoint =
            scopeType === "function"
                ? `/api/events/${eventId}/functions`
                : `/api/events/${eventId}/persons`;

        const res = await fetch(`${apiUrl}${endpoint}`, {
            headers: getAuthHeaders(),
        });

        if (!res.ok) {
            setList([]);
            return;
        }

        const data = await res.json();
        setList(Array.isArray(data) ? data : []);
    };

    const loadAllowances = async (
        eventId: number,
        scopeId: number,
        date: string
    ) => {
        if (!eventId || !scopeId) return;

        const res = await fetch(
            `${apiUrl}/api/admin/drinks-allowance?scopeType=${scopeType}&scopeId=${scopeId}&eventId=${eventId}&validDate=${date}`,
            {
                headers: getAuthHeaders(),
            }
        );

        if (!res.ok) return;

        const data = await res.json();

        const newValues: Record<number, number> = {};
        bars.forEach(b => {
            newValues[b.eventBarId] = 0;
        });

        let selectedInvoiceId: number | null = null;

        data.forEach((x: any) => {
            newValues[x.bar] = x.maxDrinks;

            if (selectedInvoiceId === null && x.invoiceId != null) {
                selectedInvoiceId = x.invoiceId;
            }
        });

        setValues(newValues);
        setInvoiceId(selectedInvoiceId);
    };

    const loadOverview = async () => {

        if (!eventId || !scopeId)
            return;

        const res = await fetch(
            `${apiUrl}/api/admin/drinks-allowance/overview?scopeType=${scopeType}&scopeId=${scopeId}&eventId=${eventId}`,
            {
                headers: getAuthHeaders()
            }
        );

        if (!res.ok)
            return;

        const data = await res.json();

        setOverview(data);
    };

    const submit = async () => {
        if (!eventId || !scopeId) return;

        const [start, end] = dateRangeValue;

        const days =
            mode === "single"
                ? [formatDate(start)]
                : dateRange(start!, end!);

        for (const day of days) {
            for (const bar of bars) {

                const amount = values[bar.eventBarId] ?? 0;

                if (amount <= 0) {
                    continue;
                }

                await fetch(`${apiUrl}/api/admin/drinks-allowance`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...getAuthHeaders(),
                    },
                    body: JSON.stringify({
                        scopeType,
                        scopeId,
                        eventId,
                        eventBarId: bar.eventBarId,
                        maxDrinks: values[bar.eventBarId] ?? 0,
                        invoiceId,
                        validDate: day,
                    }),
                });

            }
        }


        if (dateRangeValue[0]) {
            await loadAllowances(
                eventId,
                scopeId,
                formatDate(dateRangeValue[0])
            );
        }

        await loadOverview();

        alert("Saved successfully");
    };

    useEffect(() => {

        loadOverview();

    }, [
        eventId,
        scopeId,
        scopeType
    ]);

    useEffect(() => {
        if (!eventId) return;

        loadBars(eventId);
        loadInvoices(eventId);
        loadScopeList(eventId);
    }, [eventId, scopeType]);

    useEffect(() => {
        const [start] = dateRangeValue;

        if (eventId && scopeId && start && bars.length > 0) {
            loadAllowances(eventId, scopeId, formatDate(start));
        }
    }, [eventId, scopeId, dateRangeValue, scopeType, bars]);

    return (
<div>
        <HeroSectionV2
                titleEn="Drink Allowance Page"
                titleDa="Drikkevare godtgørelse"
                descriptionEn="Manage drinks given to Persons/Functions"
                descriptionDa="Her kan du give Drinks til personer/funktioner"
                imageSrc={Billede1}
            />
            <AdminBackBar
                title="Drinks"
                crumbs={[
                    { label: "Admin", to: "/admin" },
                    { label: "Drinks" } // current
                ]}
            />
        <Container size="xl" py="xl">

            <Stack gap="lg">
                {/* SELECTION PANEL */}
                <Paper shadow="sm" radius="md" p="lg" withBorder>

                    <Stack gap="md">

                        <Title order={4}>
                            Selection
                        </Title>


                        <Grid>

                            <Grid.Col span={{ base: 12, md: 4 }}>
                                <Select
                                    label="Type"
                                    value={scopeType}
                                    onChange={(v) => {
                                        setScopeType(v as ScopeType);
                                        setScopeId(0);
                                        setList([]);
                                    }}
                                    data={[
                                        {
                                            value: "function",
                                            label: "Function"
                                        },
                                        {
                                            value: "person",
                                            label: "Person"
                                        },
                                    ]}
                                />
                            </Grid.Col>


                            <Grid.Col span={{ base: 12, md: 4 }}>
                                <Select
                                    label="Event"
                                    data={events.map(e => ({
                                        value: String(e.eventID),
                                        label: e.eventName
                                    }))}
                                    value={eventId ? String(eventId) : null}
                                    onChange={(v) => {
                                        const id = Number(v);

                                        setEventId(id);
                                        setScopeId(0);
                                    }}
                                    searchable
                                    clearable
                                />
                            </Grid.Col>


                            <Grid.Col span={{ base: 12, md: 4 }}>
                                <Select
                                    label={scopeType}
                                    data={list.map((x: any) => {

                                        const isFunction =
                                            scopeType === "function";

                                        const personLabel =
                                            x.fullName ||
                                            `${x.firstName ?? ""} ${x.lastName ?? ""}`.trim() ||
                                            `Person ${x.personId}`;

                                        return {
                                            value: String(
                                                isFunction
                                                    ? x.functionId
                                                    : x.personId
                                            ),
                                            label:
                                                isFunction
                                                    ? x.functionName ??
                                                    `Function ${x.functionId}`
                                                    : personLabel
                                        };

                                    })}
                                    value={scopeId ? String(scopeId) : null}
                                    onChange={(v) =>
                                        setScopeId(Number(v))
                                    }
                                    searchable
                                    clearable
                                />
                            </Grid.Col>

                        </Grid>

                    </Stack>

                </Paper>



                {/* DATE + INVOICE */}
                <Paper shadow="sm" radius="md" p="lg" withBorder>

                    <Stack gap="md">

                        <Title order={4}>
                            Assignment details
                        </Title>


                        <Grid>

                            <Grid.Col span={{ base: 12, md: 4 }}>

                                <Stack gap="xs">

                                    <Text size="sm" fw={500}>
                                        Mode
                                    </Text>


                                    <SegmentedControl
                                        value={mode}
                                        onChange={(v) =>
                                            setMode(
                                                v as "single" | "range"
                                            )
                                        }
                                        data={[
                                            {
                                                value: "single",
                                                label: "Single day"
                                            },
                                            {
                                                value: "range",
                                                label: "Date range"
                                            }
                                        ]}
                                    />

                                </Stack>

                            </Grid.Col>


                            <Grid.Col span={{ base: 12, md: 4 }}>

                                {mode === "single" ? (

                                    <DatePickerInput
                                        label="Date"
                                        value={dateRangeValue[0]}
                                        onChange={(d) =>
                                            setDateRangeValue([d, d])
                                        }
                                    />

                                ) : (

                                    <DatePickerInput
                                        label="Date range"
                                        type="range"
                                        value={dateRangeValue}
                                        onChange={setDateRangeValue}
                                        clearable
                                    />

                                )}

                            </Grid.Col>


                            <Grid.Col span={{ base: 12, md: 4 }}>

                                <Select
                                    label="Invoice"
                                    data={invoices
                                        .filter(i => i.isActive)
                                        .map(i => ({
                                            value: String(i.invoiceId),
                                            label: i.name
                                        }))
                                    }
                                    value={
                                        invoiceId
                                            ? String(invoiceId)
                                            : null
                                    }
                                    onChange={(v) =>
                                        setInvoiceId(
                                            v
                                                ? Number(v)
                                                : null
                                        )
                                    }
                                    searchable
                                    clearable
                                />

                            </Grid.Col>

                        </Grid>

                    </Stack>

                </Paper>



                <Grid>

                    {/* CURRENT ALLOWANCES */}
                    <Grid.Col
                        span={{
                            base: 12,
                            md: 6
                        }}
                    >

                        <Paper
                            shadow="sm"
                            radius="md"
                            p="lg"
                            withBorder
                        >

                            <Stack>

                                <Title order={4}>
                                    Drinks per bar
                                </Title>

                                <Text size="sm" c="dimmed">
                                    Current allowance
                                </Text>


                                <Divider />


                                <SimpleGrid
                                    cols={{
                                        base: 1,
                                        sm: 2
                                    }}
                                >

                                    {bars.map(bar => (

                                        <Paper
                                            key={bar.eventBarId}
                                            shadow="xs"
                                            radius="md"
                                            p="md"
                                            withBorder
                                        >

                                            <Stack>

                                                <Text fw={600}>
                                                    {bar.name}
                                                </Text>


                                                <NumberInput
                                                    label="Maximum drinks"
                                                    value={
                                                        values[bar.eventBarId] ?? 0
                                                    }
                                                    min={0}
                                                    onChange={(value) =>
                                                        setValues(prev => ({
                                                            ...prev,
                                                            [bar.eventBarId]:
                                                                Number(value) || 0
                                                        }))
                                                    }
                                                />

                                            </Stack>

                                        </Paper>

                                    ))}

                                </SimpleGrid>


                            </Stack>

                        </Paper>


                    </Grid.Col>



                    {/* HISTORY */}
                    <Grid.Col
                        span={{
                            base: 12,
                            md: 6
                        }}
                    >

                        <Paper
                            shadow="sm"
                            radius="md"
                            p="lg"
                            withBorder
                        >

                            <Stack>

                                <Title order={4}>
                                    Drink history
                                </Title>


                                <Text size="sm" c="dimmed">
                                    14 days previous and 14 days upcoming
                                </Text>


                                <Divider />


                                <Stack gap="xs">

                                    {overview.map(day => (

                                        <Paper
                                            key={day.date}
                                            p="sm"
                                            radius="sm"
                                            withBorder
                                        >

                                            <Group justify="space-between">

                                                <Stack gap={2}>
                                                    <Text fw={600}>
                                                        {new Date(day.date).toLocaleDateString()}
                                                    </Text>

                                                    <Text size="sm" c="dimmed">
                                                        {day.barName ?? `Bar ${day.barId}`}
                                                    </Text>
                                                </Stack>


                                                <Text fw={700}>
                                                    {day.maxDrinks ?? 0} drinks
                                                </Text>

                                            </Group>

                                        </Paper>

                                    ))}


                                    {overview.length === 0 && (

                                        <Text c="dimmed">
                                            No allowance history found
                                        </Text>

                                    )}


                                </Stack>


                            </Stack>


                        </Paper>


                    </Grid.Col>


                </Grid>



                {/* SAVE */}
                <Group justify="flex-end">

                    <Button
                        size="md"
                        onClick={submit}
                    >
                        Assign Drink Allowances
                    </Button>

                </Group>


            </Stack>

            </Container>
        </div>
     );

};

export default AdminDrinkAllowance;